import { object, string, number, array } from 'yup';
import { InternalOptions } from 'yup/lib/types';
import { booleanPointInPolygon, polygon } from '@turf/turf';

import { CircleArea, CoordinatesOnlyPolygonArea, FilledCircleArea, FilledPolygonArea, PolygonArea, RadiusOnlyCircleArea } from '../../../../../../submodules/web-shared/types/Map.types';
import { DefaultAutocompleteOption } from '../../../../../../submodules/web-shared/api/autocomplete/types/DefaultAutocompleteOption';

import { checkIsAirportSeaportType, checkIsGlobalZoneType } from '../../utils';
import { LocationSchedule } from '../../api/types/LocationSchedule';
import { LOCATION_TYPE } from '../../api/enums/location-types';
import { ZONE_LOCATION_TYPES } from '../../consts';
import { IGNORE_EVENT_TYPES } from '../../api/enums/ignore-events-types';
import { AlternativeDeliveryLocation } from '../../api/types/AlternativeDeliveryLocation';

export interface LocationAddressForm {
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  city: string | null;
  country: string | null;
  state: string | null;
  street: string | null;
  zip: string | null;
}

export const FIELD_NAMES = {
  ID: 'id',
  LOCATION_TYPE: 'type',
  LOCATION_NAME: 'title',
  LOCATION_TITLE: 'custom_title',
  PORTAL: 'portal',
  GLOBAL: 'global',
  IGNORE_EVENTS: 'ignore_events',
  IGNORE_EVENT_TYPES: 'ignore_event_types',
  NOTES: 'notes',
  CARRIER_COMPANIES: 'carriers',
  LOCATION_ADDRESS: 'location_address',
  ADDRESS: 'address',
  LATITUDE: 'latitude',
  LONGITUDE: 'longitude',
  CITY: 'city',
  COUNTRY: 'country',
  STATE: 'state',
  STREET: 'street',
  ZIP: 'zip',
  LOCATION_SCHEDULE: 'location_schedule',
  IATA: 'iata',
  ICAO: 'icao',
  UNLOCODE: 'unlocode',
  ORIGIN_CONFIG: 'origin_config',
  RETURNED_LOGIC: 'returned_logic',
  BROKER_REQUIRED: 'broker_required',
  SHIPMENT_REF_TOGGlE: 'shipment_ref_optional',
  BROKERS: 'broker_companies',
  CARRIERS: 'carrier_companies',
  DRIVERS: 'drivers',
  CUSTOMERS: 'customer_companies',
  WORKFLOWS: 'workflow_groups',
  CHILD_ORIGINS: 'child_origins',
  PARENT_ORIGINS: 'parent_origins',
  RELAY_LOCATIONS: 'relay_locations',
  //new
  CIRCLE_AREA: 'circle_area',
  CIRCLE_AREA_RADIUS: 'radius',
  POLYGON_AREA: 'polygon_area',
  POLYGON_AREA_COORDINATES: 'lng_lat',
  AREA_BUFFER_PERCENT: 'percent',
  ALTERNATE_DELIVERY_LOCATIONS: 'alternate_delivery_locations',
} as const;

export interface LocationFormParams {
  [FIELD_NAMES.ID]?: number;
  [FIELD_NAMES.LOCATION_TYPE]: LOCATION_TYPE | null;
  [FIELD_NAMES.PORTAL]: { id: number; label: string } | null;
  [FIELD_NAMES.GLOBAL]: boolean | null;
  [FIELD_NAMES.IGNORE_EVENTS]: boolean | null;
  [FIELD_NAMES.IGNORE_EVENT_TYPES]: IGNORE_EVENT_TYPES[] | null;
  [FIELD_NAMES.LOCATION_NAME]: string | null;
  [FIELD_NAMES.LOCATION_TITLE]: string | null;
  [FIELD_NAMES.NOTES]: string | null;
  [FIELD_NAMES.CARRIER_COMPANIES]: { id: number; label: string }[] | null;
  [FIELD_NAMES.LOCATION_ADDRESS]: {
    [FIELD_NAMES.ADDRESS]: string | null;
    [FIELD_NAMES.LATITUDE]: number | null;
    [FIELD_NAMES.LONGITUDE]: number | null;
    [FIELD_NAMES.CITY]: string | null;
    [FIELD_NAMES.COUNTRY]: string | null;
    [FIELD_NAMES.STATE]: string | null;
    [FIELD_NAMES.STREET]: string | null;
    [FIELD_NAMES.ZIP]: string | null;
  };
  [FIELD_NAMES.LOCATION_SCHEDULE]: LocationSchedule | null;
  [FIELD_NAMES.IATA]: string | null;
  [FIELD_NAMES.ICAO]: string | null;
  [FIELD_NAMES.UNLOCODE]: string | null;
  [FIELD_NAMES.RELAY_LOCATIONS]: DefaultAutocompleteOption[];
  [FIELD_NAMES.ORIGIN_CONFIG]: {
    [FIELD_NAMES.RETURNED_LOGIC]: boolean | null;
    [FIELD_NAMES.BROKER_REQUIRED]: boolean | null;
    [FIELD_NAMES.SHIPMENT_REF_TOGGlE]: boolean | null;
    [FIELD_NAMES.CUSTOMERS]: { id: number; label: string }[];
    [FIELD_NAMES.BROKERS]: { id: number; label: string }[];
    [FIELD_NAMES.CARRIERS]: { id: number; label: string }[];
    [FIELD_NAMES.DRIVERS]: { id: number; label: string }[];
    [FIELD_NAMES.WORKFLOWS]: { id: number; label: string }[];
    [FIELD_NAMES.CHILD_ORIGINS]: { id: number; label: string }[];
    [FIELD_NAMES.PARENT_ORIGINS]: { id: number; label: string }[];
  };
  [FIELD_NAMES.CIRCLE_AREA]: FilledCircleArea | RadiusOnlyCircleArea | null;
  [FIELD_NAMES.POLYGON_AREA]: FilledPolygonArea | CoordinatesOnlyPolygonArea | null;
  [FIELD_NAMES.ALTERNATE_DELIVERY_LOCATIONS]: AlternativeDeliveryLocation[];
}
export const locationAddressInitValues = {
  [FIELD_NAMES.ADDRESS]: null,
  [FIELD_NAMES.LATITUDE]: null,
  [FIELD_NAMES.LONGITUDE]: null,
  [FIELD_NAMES.CITY]: null,
  [FIELD_NAMES.COUNTRY]: null,
  [FIELD_NAMES.STATE]: null,
  [FIELD_NAMES.STREET]: null,
  [FIELD_NAMES.ZIP]: null,
};

export const originConfigInitValues = {
  [FIELD_NAMES.RETURNED_LOGIC]: false,
  [FIELD_NAMES.BROKER_REQUIRED]: false,
  [FIELD_NAMES.SHIPMENT_REF_TOGGlE]: false,
  [FIELD_NAMES.CUSTOMERS]: [],
  [FIELD_NAMES.BROKERS]: [],
  [FIELD_NAMES.CARRIERS]: [],
  [FIELD_NAMES.DRIVERS]: [],
  [FIELD_NAMES.WORKFLOWS]: [],
  [FIELD_NAMES.CHILD_ORIGINS]: [],
  [FIELD_NAMES.PARENT_ORIGINS]: []
};

export const initialValues: LocationFormParams = {
  [FIELD_NAMES.LOCATION_TYPE]: null,
  [FIELD_NAMES.PORTAL]: null,
  [FIELD_NAMES.GLOBAL]: true,
  [FIELD_NAMES.IGNORE_EVENTS]: false,
  [FIELD_NAMES.IGNORE_EVENT_TYPES]: [],
  [FIELD_NAMES.LOCATION_NAME]: '',
  [FIELD_NAMES.LOCATION_TITLE]: '',
  [FIELD_NAMES.NOTES]: '',
  [FIELD_NAMES.CARRIER_COMPANIES]: [],
  [FIELD_NAMES.LOCATION_ADDRESS]: locationAddressInitValues,
  [FIELD_NAMES.LOCATION_SCHEDULE]: null,
  [FIELD_NAMES.IATA]: '',
  [FIELD_NAMES.ICAO]: '',
  [FIELD_NAMES.UNLOCODE]: '',
  [FIELD_NAMES.RELAY_LOCATIONS]: [],
  [FIELD_NAMES.ORIGIN_CONFIG]: originConfigInitValues,
  [FIELD_NAMES.CIRCLE_AREA]: null,
  [FIELD_NAMES.POLYGON_AREA]: null,
  [FIELD_NAMES.ALTERNATE_DELIVERY_LOCATIONS]: [],
};

export const seaportCircleAreaInitValues: CircleArea = {
  [FIELD_NAMES.CIRCLE_AREA_RADIUS]: 2,
  [FIELD_NAMES.AREA_BUFFER_PERCENT]: 30
};

export const addressBaseName = FIELD_NAMES.LOCATION_ADDRESS;
export const originConfigBaseName = FIELD_NAMES.ORIGIN_CONFIG;

const maxNameLength = 200;
const maxNoteLength = 300;

const geofenceValidator = function (this: InternalOptions, value: unknown) {
  const geofence = value as LocationFormParams['polygon_area'];

  const longitude = this.parent[FIELD_NAMES.LOCATION_ADDRESS][FIELD_NAMES.LONGITUDE];
  const latitude = this.parent[FIELD_NAMES.LOCATION_ADDRESS][FIELD_NAMES.LATITUDE];
  if (geofence && longitude && latitude) {
    const locationCoordinates = [longitude, latitude];
    const isPointInPolygon = booleanPointInPolygon(locationCoordinates, polygon([geofence.lng_lat]));
    return isPointInPolygon;
  }
  return true;
};

export const validationSchema = object({
  [FIELD_NAMES.LOCATION_TYPE]: string().nullable().required(),
  [FIELD_NAMES.PORTAL]: object({ id: number() }).when([FIELD_NAMES.LOCATION_TYPE, FIELD_NAMES.GLOBAL], {
    is: (value: LOCATION_TYPE, isGlobal: boolean) => (
      checkIsAirportSeaportType(value) ||
      (checkIsGlobalZoneType(value) && isGlobal)),
    then: field => field.nullable(),
    otherwise: field => field.nullable().required()
  }),
  [FIELD_NAMES.LOCATION_NAME]: string().nullable().required().max(maxNameLength),
  [FIELD_NAMES.LOCATION_TITLE]: string().nullable().max(maxNameLength),
  [FIELD_NAMES.IGNORE_EVENT_TYPES]: array().when(FIELD_NAMES.IGNORE_EVENTS, {
    is: true,
    then: field => field.of(string()).min(1).required()
  }),
  [FIELD_NAMES.NOTES]: string().nullable().max(maxNoteLength, 'validation_note_field'),
  [FIELD_NAMES.LOCATION_ADDRESS]: object({
    [FIELD_NAMES.ADDRESS]: string().nullable().required(),
    [FIELD_NAMES.LATITUDE]: number().nullable().min(-90).max(90).required(),
    [FIELD_NAMES.LONGITUDE]: number().nullable().min(-180).max(180).required(),
  }),
  [FIELD_NAMES.IATA]: string().when(FIELD_NAMES.LOCATION_TYPE, {
    is: LOCATION_TYPE.AIRPORT,
    then: field => field.nullable().required(),
    otherwise: field => field.nullable()
  }),
  [FIELD_NAMES.ICAO]: string().when(FIELD_NAMES.LOCATION_TYPE, {
    is: LOCATION_TYPE.AIRPORT,
    then: field => field.nullable().required(),
    otherwise: field => field.nullable()
  }),
  [FIELD_NAMES.UNLOCODE]: string().matches(/^[A-Z]+$/g, 'validation_uppercase_letters').when(FIELD_NAMES.LOCATION_TYPE, {
    is: LOCATION_TYPE.SEAPORT,
    then: field => field.nullable().required(),
    otherwise: field => field.nullable()
  }),
  [FIELD_NAMES.CIRCLE_AREA]: object().nullable().when([FIELD_NAMES.LOCATION_TYPE, FIELD_NAMES.POLYGON_AREA], {
    is: (locationType: LOCATION_TYPE, polygonArea: PolygonArea | null) => {
      return ZONE_LOCATION_TYPES.includes(locationType) && !polygonArea?.lng_lat?.length;
    },
    then: field => field.required(),
  }),
  [FIELD_NAMES.POLYGON_AREA]: object().test(
    'geofence-validator',
    'validation_center_in_geofence',
    geofenceValidator
  ).nullable(),
  [FIELD_NAMES.ALTERNATE_DELIVERY_LOCATIONS]: array().max(5),
});

export const textAreaRows = 5;